import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { usePlacementEnvironment } from "../../api/queries/placement-environment/usePlacementEnvironment";
import {
  PlacementEnvironmentDetailChecklist,
  PlacementEnvironmentDetailChecklistStage,
  PlacementEnvironmentDetailChecklistStageTask,
} from "../../api/types/placement-environment";
import { Splitter, SplitterPanel } from "primereact/splitter";
import { Panel, PanelHeaderTemplateOptions } from "primereact/panel";
import {
  PersonOption,
  getResponsibleAvatars,
  getStagePercents,
  usePersonOptions,
} from "./shared";
import { formatDate, orderIndexSort } from "../../common/utils";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { useUserChecklistTaskAdd } from "../../api/queries/user-checklists/useUserChecklistTaskAdd";
import { InputText } from "primereact/inputtext";
import moment from "moment";
import { UserChecklistTaskAddPayload } from "../../api/types/user-checklist";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useUpdateInteraction } from "../../api/queries/interactions/useUpdateInteraction";
import {
  InteractionSortPayload,
  UpdateInteractionPayload,
} from "../../api/types/interaction";
import { none } from "../common";
import {
  DataTable,
  DataTableRowReorderEvent,
  DataTableSelectEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Editor, defaultEditorModules } from "../Editor";
import { useInteractionSort } from "../../api/queries/interactions/useInteractionSort";
import { FormEvent, Nullable } from "primereact/ts-helpers";

export default function PlacementEnvironmentEdit() {
  const { uuid, institutionUuid } = useParams();
  const queryClient = useQueryClient();
  const { data: placementEnvironment } = usePlacementEnvironment(uuid);
  const userChecklistTaskAdd = useUserChecklistTaskAdd();
  const interactionSort = useInteractionSort();

  const [selectedStageUuid, setSelectedStageUuid] = useState<string>("");
  const onStageClick = (uuid: string) => {
    setSelectedStageUuid(uuid);
    setSelectedRow(undefined);
    resetNewTaskForm();
  };

  const [selectedRow, setSelectedRow] =
    useState<PlacementEnvironmentDetailChecklistStageTask>();
  const updateInteraction = useUpdateInteraction(selectedRow?.id);

  const checklist = placementEnvironment?.data.results.checklists.find(
    (checklist) =>
      checklist.user_checklist_institutions[0].id === institutionUuid,
  );

  const tPanelHeader = (
    options: PanelHeaderTemplateOptions,
    stage: PlacementEnvironmentDetailChecklistStage,
  ) => {
    const isStageComplete = stage.checklist_tasks_user.every(
      (task) => task.completed,
    );

    return (
      <div
        className={`${options.className} justify-content-start cursor-pointer`}
        onClick={() => onStageClick(stage.id)}
      >
        <i
          className={`fa-regular fa-${
            isStageComplete ? "circle-check" : "circle"
          } mr-3`}
        />
        <Badge value={stage.name} severity={"warning"} />
        <span className="ml-auto">{getStagePercents(stage)}% complete</span>
      </div>
    );
  };

  // new task
  const personOptions = usePersonOptions();

  const resetNewTaskForm = () => {
    setNewTaskFormData(null);
    setTaskName("");
    setTaskDescription("");
    setTaskCompleted(false);
    setTaskDueDate(null);
    setTaskRemind(false);
    setTaskRemindAt(null);
    setTaskPersonResponsible([]);
    setTaskCompleteWith([]);
    setComment("");
  };

  const [newTaskFormData, setNewTaskFormData] =
    useState<NewTaskFormData | null>(null);

  const [taskName, setTaskName] = useState<string>("");
  const onTaskNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setTaskName(event.target.value);

  const [taskDescription, setTaskDescription] = useState<string>("");
  const onTaskDescriptionChange = (event: ChangeEvent<HTMLInputElement>) =>
    setTaskDescription(event.target.value);

  const [taskCompleted, setTaskCompleted] = useState<boolean>(false);
  const onTaskCompletedChange = (event: CheckboxChangeEvent) =>
    setTaskCompleted(event.checked || false);

  const [taskDueDate, setTaskDueDate] = useState<Nullable<Date>>(null);
  const onTaskDueDateChange = (event: FormEvent<Date>) =>
    setTaskDueDate(event.value as Date);

  const [taskRemind, setTaskRemind] = useState<boolean>(false);
  const onTaskRemindChange = (event: CheckboxChangeEvent) =>
    setTaskRemind(event.checked || false);

  const [taskRemindAt, setTaskRemindAt] = useState<Nullable<Date>>(null);
  const onTaskRemindAtChange = (event: FormEvent<Date>) =>
    setTaskRemindAt(event.value as Date);

  const [taskPersonResponsible, setTaskPersonResponsible] = useState<
    PersonOption[]
  >([]);
  const onTaskPersonResponsibleChange = (event: MultiSelectChangeEvent) =>
    setTaskPersonResponsible(event.value);

  const [taskCompleteWith, setTaskCompleteWith] = useState<PersonOption[]>([]);
  const onTaskCompleteWithChange = (event: MultiSelectChangeEvent) =>
    setTaskCompleteWith(event.value);

  const [comment, setComment] = useState<string>("");
  const onCommentChange = (value: string) => setComment(value);

  const onNewTaskClick = (
    checklist: PlacementEnvironmentDetailChecklist,
    stage: PlacementEnvironmentDetailChecklistStage,
  ) => {
    resetNewTaskForm();
    setSelectedRow(undefined);
    setNewTaskFormData({
      institutionId: checklist.user_checklist_institutions[0].id,
      checklistId: checklist.id,
      stageId: stage.id,
    });
  };

  const onNewTaskFormSumit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!uuid || !newTaskFormData) {
      return;
    }

    const selectedStage = checklist?.checklist_stages.find(
      (stage) => stage.id === selectedStageUuid,
    );

    let maxOrderIndex = 0;

    selectedStage?.checklist_tasks_user.forEach((task) => {
      if (task.order_index > maxOrderIndex) {
        maxOrderIndex = task.order_index;
      }
    });

    const payload: UserChecklistTaskAddPayload = {
      placement_environment: uuid,
      institution: newTaskFormData.institutionId,
      checklist: newTaskFormData.checklistId,
      stage: newTaskFormData.stageId,
      name: taskName,
      description: taskDescription,
      order_index: maxOrderIndex + 1,
      completed: taskCompleted,
      due_date: null,
      remind: taskRemind,
      remind_at: null,
      responsible_users: taskPersonResponsible
        .filter((option: PersonOption) => option.type === "user")
        .map((option) => option.id),
      responsible_representatives: taskPersonResponsible
        .filter((option: PersonOption) => option.type === "representative")
        .map((option) => option.id),
      complete_with_users: taskCompleteWith
        .filter((option: PersonOption) => option.type === "user")
        .map((option) => option.id),
      complete_with_representatives: taskCompleteWith
        .filter((option: PersonOption) => option.type === "representative")
        .map((option) => option.id),
      //
      interaction_types: [],
      checklist_task: null,
      parent_task: null,
      time_spent: 0,
      responsible_people: [],
      complete_with_people: [],
      comment,
    };

    if (taskDueDate) {
      payload.due_date = moment(taskDueDate as Date).format("YYYY-MM-DD");
    }

    if (taskRemindAt) {
      payload.remind_at = moment(taskRemindAt as Date).format("YYYY-MM-DD");
    }

    const timezoneOffset = new Date().getTimezoneOffset();

    if (timezoneOffset) {
      payload.timezone_offset = timezoneOffset;
    }

    await userChecklistTaskAdd.mutateAsync(payload);

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid));
    resetNewTaskForm();
  };

  // update
  const onUpdateTaskFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const payload: UpdateInteractionPayload = {
      name: taskName,
      description: taskDescription,
      completed: taskCompleted,
      due_date: null,
      order_index: selectedRow?.order_index || 0,
      remind: taskRemind,
      remind_at: null,
      responsible_users: taskPersonResponsible
        .filter((option: PersonOption) => option.type === "user")
        .map((option) => option.id),
      responsible_representatives: taskPersonResponsible
        .filter((option: PersonOption) => option.type === "representative")
        .map((option) => option.id),
      complete_with_users: taskCompleteWith
        .filter((option: PersonOption) => option.type === "user")
        .map((option) => option.id),
      complete_with_representatives: taskCompleteWith
        .filter((option: PersonOption) => option.type === "representative")
        .map((option) => option.id),
      //
      interaction_types: [],
      time_spent: 0,
      responsible_people: [],
      complete_with_people: [],
      comment,
    };

    if (taskDueDate) {
      payload.due_date = moment(taskDueDate as Date).format("YYYY-MM-DD");
    }

    if (taskRemindAt) {
      payload.remind_at = moment(taskRemindAt as Date).format("YYYY-MM-DD");
    }

    const timezoneOffset = new Date().getTimezoneOffset();

    if (timezoneOffset) {
      payload.timezone_offset = timezoneOffset;
    }

    await updateInteraction.mutateAsync(payload);

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid));
  };

  useEffect(() => {
    if (selectedRow) {
      setTaskName(selectedRow.name);
      setTaskDescription(selectedRow.description);
      setTaskCompleted(selectedRow.completed);
      setTaskDueDate(
        selectedRow.due_date ? moment(selectedRow.due_date).toDate() : null,
      );
      setTaskRemind(selectedRow.remind);

      // setNewTaskRemindAt(
      //   selectedRow.remind_at ? moment(selectedRow.remind_at).toDate() : null
      // );

      setTaskPersonResponsible(
        personOptions.filter((option) =>
          selectedRow.interaction_people.some(
            (person) =>
              (person.representative?.id === option.id ||
                person.user?.id === option.id) &&
              person.person_type === "responsible",
          ),
        ),
      );
      setTaskCompleteWith(
        personOptions.filter((option) =>
          selectedRow.interaction_people.some(
            (person) =>
              (person.representative?.id === option.id ||
                person.user?.id === option.id) &&
              person.person_type === "complete-with",
          ),
        ),
      );
      setComment(selectedRow.comment || "");
    }
  }, [selectedRow, personOptions]);

  const onRowSelect = (data: DataTableSelectEvent) => {
    setSelectedRow(data.data);
  };

  const onRowReorder = async (
    data: DataTableRowReorderEvent<
      PlacementEnvironmentDetailChecklistStageTask[]
    >,
  ) => {
    const sorting = data.value.map((task, index) => ({
      id: task.id,
      order_index: index + 1,
    }));

    const payload: InteractionSortPayload = {
      placement_environment: uuid || "",
      checklist: checklist?.id || "",
      institution: institutionUuid || "",
      sorting: sorting,
    };

    await interactionSort.mutateAsync(payload);

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid));
  };

  const tMilestoneTask = (
    row: PlacementEnvironmentDetailChecklistStageTask,
  ) => {
    let taskIcon = "";

    if (row.is_template) {
      taskIcon = "fa-circle";
    } else {
      taskIcon = "fa-square";
    }

    if (row.completed) {
      taskIcon += "-check";
    }

    return (
      <>
        <i
          className={`mr-1 fa-regular ${
            row.is_template ? "text-yellow-600" : ""
          } ${taskIcon}`}
        />
        {row.name}
      </>
    );
  };

  const tDue = (row: PlacementEnvironmentDetailChecklistStageTask) =>
    row.due_date ? formatDate(row.due_date) : none;

  const tCompleted = (row: PlacementEnvironmentDetailChecklistStageTask) =>
    row.completed && row.completed_at ? formatDate(row.completed_at) : none;

  const tResponsible = (row: PlacementEnvironmentDetailChecklistStageTask) =>
    getResponsibleAvatars(personOptions, row.interaction_people);

  return (
    <div className={"col-12"}>
      <Splitter style={{ minHeight: "calc(100vh - 100px)" }}>
        <SplitterPanel>
          <h4 className="m-0 p-3 border-300 border-bottom-1">
            {checklist?.user_checklist_institutions[0].name} status
          </h4>

          <div className="p-3">
            {checklist?.checklist_stages.sort(orderIndexSort).map((stage) => (
              <Panel
                key={stage.id}
                headerTemplate={(options: PanelHeaderTemplateOptions) =>
                  tPanelHeader(options, stage)
                }
                className="mb-3 p-panel-custom"
              >
                {selectedStageUuid === stage.id && (
                  <>
                    <DataTable
                      value={stage.checklist_tasks_user.sort(orderIndexSort)}
                      rowHover={true}
                      onRowSelect={onRowSelect}
                      selectionMode="single"
                      selection={selectedRow}
                      dataKey="id"
                      className="p-datatable-sm"
                      reorderableRows={true}
                      onRowReorder={onRowReorder}
                    >
                      <Column rowReorder style={{ width: "3rem" }} />
                      <Column header="MILESTONE TASKS" body={tMilestoneTask} />
                      <Column header="DUE" body={tDue} />
                      <Column header="COMPLETED" body={tCompleted} />
                      <Column header="RESPONSIBLE" body={tResponsible} />
                    </DataTable>

                    <Button
                      type="button"
                      label="New activity"
                      className="p-button-sm p-button-secondary p-button-text w-full text-left"
                      outlined={true}
                      onClick={() => onNewTaskClick(checklist, stage)}
                    />
                  </>
                )}
              </Panel>
            ))}
          </div>
        </SplitterPanel>

        <SplitterPanel>
          <h4 className="m-0 p-3 border-300 border-bottom-1">
            {newTaskFormData ? (
              "New activity"
            ) : selectedRow ? (
              `Review ${selectedRow.name}`
            ) : (
              <i>&nbsp;</i>
            )}
          </h4>

          <div className="p-3">
            {!newTaskFormData && !selectedRow && (
              <div className="border-1 border-300 border-round p-3 surface-ground text-center h-full">
                <h2>No Task is Selected</h2>
                <p>
                  To view details, select an stage and task / activity in the
                  left hand panel.
                </p>
              </div>
            )}

            {(newTaskFormData || selectedRow) && (
              <form
                onSubmit={
                  newTaskFormData ? onNewTaskFormSumit : onUpdateTaskFormSubmit
                }
                className={"border-1 border-300 border-round p-3"}
              >
                <div className="p-fluid">
                  <div className="grid formgrid">
                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <InputText
                          value={taskName}
                          onChange={onTaskNameChange}
                        />
                        <label>Name</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <InputText
                          value={taskDescription}
                          onChange={onTaskDescriptionChange}
                        />
                        <label>Description</label>
                      </div>
                    </div>
                  </div>

                  <div className="grid formgrid">
                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <MultiSelect
                          options={personOptions}
                          value={taskPersonResponsible}
                          onChange={onTaskPersonResponsibleChange}
                        />
                        <label>Person responsible *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <MultiSelect
                          options={personOptions}
                          value={taskCompleteWith}
                          onChange={onTaskCompleteWithChange}
                        />
                        <label>Complete with *</label>
                      </div>
                    </div>
                  </div>

                  <div className="field-checkbox">
                    <Checkbox
                      checked={taskCompleted}
                      onChange={onTaskCompletedChange}
                      inputId="taskCompleted"
                    />
                    <label htmlFor="taskCompleted">Completed</label>
                  </div>

                  <div className="grid formgrid">
                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <Calendar
                          value={taskDueDate}
                          onChange={onTaskDueDateChange}
                        />
                        <label>Due date *</label>
                      </div>
                    </div>

                    <div className="field col-12 md:col-6 mt-3">
                      <div className="p-float-label">
                        <Calendar
                          value={taskRemindAt}
                          onChange={onTaskRemindAtChange}
                          disabled={!taskRemind}
                        />
                        <label>Date of reminder</label>
                      </div>
                    </div>
                  </div>

                  <div className="field-checkbox">
                    <Checkbox
                      checked={taskRemind}
                      onChange={onTaskRemindChange}
                      inputId="taskRemind"
                    />
                    <label htmlFor="taskRemind">Remind</label>
                  </div>

                  <div className="px-3 py-1 mb-3 surface-100 border-round-sm">
                    <div className="field">
                      <label className="text-xs text-500">Comment</label>
                      <Editor
                        content={comment}
                        onChange={onCommentChange}
                        modules={defaultEditorModules}
                      />
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <Button
                    className="p-button-sm p-button-main"
                    label="Save"
                    disabled={
                      userChecklistTaskAdd.isLoading ||
                      updateInteraction.isLoading
                    }
                  />
                </div>
              </form>
            )}
          </div>
        </SplitterPanel>
      </Splitter>
    </div>
  );
}

interface NewTaskFormData {
  institutionId: string;
  stageId: string;
  checklistId: string;
}
